import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AppDispatch, RootState } from "../../../../../store/store";
import { Checkbox, MenuItem, Select, SelectChangeEvent } from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import PurchaseLayout from "./components/PurchaseLayout";
import {
  getAuthListing,
  getAuthListings,
  getListing,
} from "../../../../../apis/listings/buyer";
import ConfirmationModal from "../../../../common/components/ConfirmationModal";
import {
  getBuyerTypes,
  getMeansOfIdentification,
  getPaymentTypes,
  getPurchaseOwners,
} from "../../../../../apis/others";
import { getCountriesList } from "../../../../../apis/locations";
import { postPurchaseRequest } from "../../../../../apis/buyer/payment";
import BrowseDeviceField from "../../../../common/components/BrowseDeviceField";
import DropdownAutocompleteField from "../../../../common/components/DropdownAutocompleteField";
import { ListingInterface } from "../../../../../types";
import {
  fetchAuthListingSuccess,
  fetchListingsSuccess,
} from "../../../../../store/slices/buyer/listingsSlice";

const Purchase = () => {
  const { lid: listingId } = useParams();

  const initialInputs = {
    owner: "",
    name_as_expected_on_property_doc: "",
    address: "",
    country_of_residence: "",
    buyer_type: "",
    means_of_identification: "",
    moi_image: "",
    proprtyCode: listingId || "",
    payment_type: "",
    certificate_of_incorporation: "",
    letter_of_authorization: "",
    board_of_directors_resolution: "",
  };

  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const listing = useSelector((state: RootState) => state.listings);

  const [inputs, setInputs] = useState<Record<string, string>>(initialInputs);
  const [utitlityBill, setUtitlityBill] = useState<File | null>();
  const [certificateOfIncorporation, setCertificateOfIncorporation] =
    useState<File | null>();
  const [letterOfAuthorization, setLetterOfAuthorization] =
    useState<File | null>();
  const [boardOfDirectorsResolution, setBoardOfDirectorsResolution] =
    useState<File | null>();
  const [acceptTerms, setAccepTerms] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [referenceId, setRefeferenceId] = useState("");

  const { data: listingData, isLoading: isListingLoading } = useQuery({
    queryKey: ["listing", { listingId, getRef: true }],
    queryFn: getAuthListing,
  });

  useEffect(() => {
    if (listingData?.lid && listingData?.lid !== listing.listing?.lid) {
      dispatch(fetchAuthListingSuccess(listingData));
    }
  }, [listingData]);

  const { data: purchaseOwners = [], isLoading: isOwnersLoading } = useQuery({
    queryKey: ["purchaseOwners"],
    queryFn: getPurchaseOwners,
  });
  const { data: buyerTypes = [], isLoading: isBuyerTypesLoading } = useQuery({
    queryKey: ["buyerTypes"],
    queryFn: getBuyerTypes,
  });
  const { data: means_of_identification = [], isLoading: isMoiLoading } =
    useQuery({
      queryKey: ["means_of_identification"],
      queryFn: getMeansOfIdentification,
    });
  const { data: paymentTypes = [], isLoading: isPaymentTypesLoading } =
    useQuery({
      queryKey: ["paymentTypes"],
      queryFn: getPaymentTypes,
    });
  const { data: countries = [], isLoading: isCountriesLoading } = useQuery({
    queryKey: ["countries"],
    queryFn: getCountriesList,
  });

  const sendPurchaseRequest = useMutation({
    mutationFn: postPurchaseRequest,
    onSuccess: (data) => {
      if (data !== "error") {
        setRefeferenceId(data?.reference_id);
        setShowSuccessModal(true);
      }
    },
    onError: (error) => {
      // TODO: Handle error, e.g., show an error message to the user.
    },
  });

  const handleInputs = (
    e: React.ChangeEvent<HTMLInputElement> | SelectChangeEvent<string>
  ) => {
    setInputs({ ...inputs, [e.target.name]: e.target.value });

    if (e.target.name === "moi_image") {
      const target = e.target as HTMLInputElement;
      setUtitlityBill(target.files?.[0]);
    }
    if (e.target.name === "certificate_of_incorporation") {
      const target = e.target as HTMLInputElement;
      setCertificateOfIncorporation(target.files?.[0]);
    }
    if (e.target.name === "letter_of_authorization") {
      const target = e.target as HTMLInputElement;
      setLetterOfAuthorization(target.files?.[0]);
    }
    if (e.target.name === "board_of_directors_resolution") {
      const target = e.target as HTMLInputElement;
      setBoardOfDirectorsResolution(target.files?.[0]);
    }
  };

  const isInputsValid = Object.entries(inputs).every(([key, value]) => {
    const isValidInd =
      (inputs.buyer_type !== "ind") ===
      ["means_of_identification", "moi_image"].includes(key);

    const isValidCorp =
      (inputs.buyer_type !== "corp") ===
        [
          "certificate_of_incorporation",
          "letter_of_authorization",
          "board_of_directors_resolution",
        ].includes(key) &&
      (value.trim?.() || value);

    const isValid =
      (value.trim?.() || value) != "" || isValidInd || isValidCorp;

    return isValid;
  });

  const saveInfo = () => {
    const bodyFormData = new FormData();

    for (let key of Object.entries(inputs) as [string, any][]) {
      if (key[0] === "moi_image" && utitlityBill) {
        bodyFormData.append("moi_image", utitlityBill);
      } else if (
        key[0] === "certificate_of_incorporation" &&
        certificateOfIncorporation
      ) {
        bodyFormData.append(
          "certificate_of_incorporation",
          certificateOfIncorporation
        );
      } else if (
        key[0] === "letter_of_authorization" &&
        letterOfAuthorization
      ) {
        bodyFormData.append("letter_of_authorization", letterOfAuthorization);
      } else if (
        key[0] === "board_of_directors_resolution" &&
        boardOfDirectorsResolution
      ) {
        bodyFormData.append(
          "board_of_directors_resolution",
          boardOfDirectorsResolution
        );
      } else {
        bodyFormData.append(key[0], key[1]);
      }
    }

    if (listingId) {
      bodyFormData.append("listingId", listingId);
    }

    sendPurchaseRequest.mutate(bodyFormData);
  };

  const showPreview = () => {};

  return (
    <>
      <PurchaseLayout heading="Purchase request">
        <div>
          <div className="card-shadow flex-1 p-[30px] flex flex-col gap-y-[35px] sm:min-w-[700px] rounded-[8px]">
            {isListingLoading ? (
              <div className="text-[14px]">Loading...</div>
            ) : listingData?.purchase_request?.status !== "valid" ? (
              <>
                <div className="purchase_form_field">
                  <div className="flex-1 sm:max-w-[50%]">
                    <label htmlFor="owner" className="custom__label">
                      Owner{" "}
                      <span className="text-[#CD0101] font-semibold">*</span>
                    </label>
                    <Select
                      name="owner"
                      value={inputs.owner}
                      onChange={handleInputs}
                      className="standard_form_input"
                      IconComponent={ExpandMoreRoundedIcon}
                    >
                      {purchaseOwners.map(({ name, value }) => (
                        <MenuItem key={value} value={value}>
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                  </div>
                  <div className="sm:min-w-[400px]">
                    <label
                      htmlFor="name_as_expected_on_property_doc"
                      className="custom__label"
                    >
                      Name as expected on the Property document{" "}
                      <span className="text-[#CD0101] font-semibold">*</span>
                    </label>
                    <input
                      type="text"
                      name="name_as_expected_on_property_doc"
                      value={inputs.name_as_expected_on_property_doc}
                      onChange={handleInputs}
                      className="standard_form_input"
                    />
                  </div>
                </div>
                <div className="purchase_form_field">
                  <div className="flex-1">
                    <label htmlFor="address" className="custom__label">
                      Address{" "}
                      <span className="text-[#CD0101] font-semibold">*</span>
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={inputs.address}
                      onChange={handleInputs}
                      className="standard_form_input"
                    />
                  </div>
                  <div className="flex-1">
                    <label
                      htmlFor="country_of_residence"
                      className="custom__label"
                    >
                      Country of residence{" "}
                      <span className="text-[#CD0101] font-semibold">*</span>
                    </label>
                    <DropdownAutocompleteField
                      id="country_of_residence"
                      name="country_of_residence"
                      onChange={handleInputs}
                      value={inputs.country_of_residence}
                      options={countries}
                      placeholder="Select country"
                      getOptionLabel={(option) => option.name || ""}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          <span className="text-[16px] mr-[10px]">
                            {option.flag}
                          </span>{" "}
                          {option.name}
                        </li>
                      )}
                    />
                  </div>
                </div>
                <div className="purchase_form_field">
                  <div className="flex-1">
                    <label htmlFor="buyer_type" className="custom__label">
                      Buyer type{" "}
                      <span className="text-[#CD0101] font-semibold">*</span>
                    </label>
                    <Select
                      name="buyer_type"
                      value={inputs.buyer_type}
                      onChange={handleInputs}
                      className="standard_form_input"
                      IconComponent={ExpandMoreRoundedIcon}
                    >
                      {buyerTypes.map(({ name, value }) => (
                        <MenuItem key={value} value={value}>
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                  </div>
                  {inputs.buyer_type === "corp" ? (
                    <div className="flex-1">
                      <label
                        htmlFor="certificate_of_incorporation"
                        className="custom__label"
                      >
                        Certificate of Incorporation{" "}
                        <span className="text-[#CD0101] font-semibold">*</span>
                      </label>
                      <BrowseDeviceField
                        id="certificate_of_incorporation"
                        name="certificate_of_incorporation"
                        className="standard_form_input"
                        onChange={handleInputs}
                        value={inputs.certificate_of_incorporation}
                        accept=".png,.jpeg,.jpg,.bmp,.pdf"
                      />
                    </div>
                  ) : (
                    <div className="flex-1">
                      <label htmlFor="tin" className="custom__label">
                        Tax Identification Number{" "}
                        <span className="opacity-30 text-[12px]">
                          (Optional)
                        </span>
                      </label>
                      <input
                        type="text"
                        name="tin"
                        value={inputs.tin}
                        onChange={handleInputs}
                        className="standard_form_input"
                      />
                    </div>
                  )}
                </div>

                {inputs.buyer_type === "corp" ? (
                  <div className="purchase_form_field">
                    <div className="flex-1">
                      <label
                        htmlFor="letter_of_authorization"
                        className="custom__label"
                      >
                        Letter of Authorization{" "}
                        <span className="text-[#CD0101] font-semibold">*</span>
                      </label>
                      <BrowseDeviceField
                        id="letter_of_authorization"
                        name="letter_of_authorization"
                        className="standard_form_input"
                        onChange={handleInputs}
                        value={inputs.letter_of_authorization}
                        accept=".png,.jpeg,.jpg,.bmp,.pdf"
                      />
                    </div>
                    <div className="flex-1">
                      <label
                        htmlFor="board_of_directors_resolution"
                        className="custom__label"
                      >
                        Board of Directors Resolution{" "}
                        <span className="text-[#CD0101] font-semibold">*</span>{" "}
                      </label>
                      <BrowseDeviceField
                        id="board_of_directors_resolution"
                        name="board_of_directors_resolution"
                        className="standard_form_input"
                        onChange={handleInputs}
                        value={inputs.board_of_directors_resolution}
                        accept=".png,.jpeg,.jpg,.bmp,.pdf"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="purchase_form_field">
                    <div className="flex-1">
                      <label
                        htmlFor="means_of_identification"
                        className="custom__label"
                      >
                        Means of identification{" "}
                        <span className="text-[#CD0101] font-semibold">*</span>
                      </label>
                      <Select
                        name="means_of_identification"
                        value={inputs.means_of_identification}
                        onChange={handleInputs}
                        className="standard_form_input"
                        IconComponent={ExpandMoreRoundedIcon}
                      >
                        {means_of_identification.map(({ name, value }) => (
                          <MenuItem key={value} value={value}>
                            {name}
                          </MenuItem>
                        ))}
                      </Select>
                    </div>
                    <div className="flex-1">
                      <label htmlFor="moi_image" className="custom__label">
                        ID Upload{" "}
                        <span className="text-[#CD0101] font-semibold">*</span>{" "}
                        <span className="opacity-30 text-[12px]">
                          (png, jpg, pdf)
                        </span>
                      </label>
                      <BrowseDeviceField
                        id="moi_image"
                        name="moi_image"
                        className="standard_form_input"
                        onChange={handleInputs}
                        value={inputs.moi_image}
                        accept=".png,.jpeg,.jpg,.bmp,.pdf"
                      />
                    </div>
                  </div>
                )}

                <div className="purchase_form_field">
                  <div className="flex-1">
                    <label htmlFor="proprtyCode" className="custom__label">
                      Unique property code{" "}
                      <span className="text-[#CD0101] font-semibold">*</span>
                    </label>
                    <input
                      type="text"
                      name="proprtyCode"
                      value={listing?.listing?.listing_code || ""}
                      onChange={handleInputs}
                      className="standard_form_input"
                      disabled
                    />
                  </div>
                  <div className="flex-1">
                    <label htmlFor="payment_type" className="custom__label">
                      Payment type{" "}
                      <span className="text-[#CD0101] font-semibold">*</span>
                    </label>
                    <Select
                      name="payment_type"
                      value={inputs.payment_type}
                      onChange={handleInputs}
                      className="standard_form_input"
                      IconComponent={ExpandMoreRoundedIcon}
                    >
                      {paymentTypes.map(({ name, value }) => (
                        <MenuItem key={value} value={value}>
                          {name}
                        </MenuItem>
                      ))}{" "}
                    </Select>
                  </div>
                </div>

                <div className="flex gap-x-[15px]">
                  {/* <input type="checkbox" id="terms" /> */}
                  <Checkbox
                    id="terms"
                    name="terms"
                    value={inputs.terms}
                    onChange={() =>
                      setAccepTerms((currentState) => !currentState)
                    }
                    color="primary"
                    // checked={isItemSelected}
                  />

                  <label
                    htmlFor="terms"
                    className="custom__label font-light text-[#5E5E5E]"
                    style={{ marginBottom: 0 }}
                  >
                    By agreeing to this purchase, you acknowledge that you have
                    reviewed and accepted the{" "}
                    <a
                      href={`${process.env.REACT_APP_FE_BASEURL}/terms-and-conditions`}
                      className="text-[#066D0E]"
                    >
                      terms and conditions
                    </a>{" "}
                    of sale.
                  </label>
                </div>

                <button
                  className="filled-button w-full"
                  disabled={!isInputsValid || !acceptTerms}
                  onClick={saveInfo}
                >
                  SUBMIT PURCHASE REQUEST
                </button>
              </>
            ) : (
              <div className="text-[14px]">
                Purchase request already submitted for this listing. View{" "}
                <a
                  href={`${process.env.REACT_APP_FE_BASEURL}/purchase/invoice/${listingData?.purchase_request?.reference_id}`}
                  style={{
                    color: "#066D0E",
                    textDecoration: "underline",
                  }}
                >
                  Invoice
                </a>
                , or proceed to{" "}
                <a
                  href={`${process.env.REACT_APP_FE_BASEURL}/purchase/checkout/${listing.listing?.lid}`}
                  style={{
                    color: "#066D0E",
                    textDecoration: "underline",
                  }}
                >
                  Checkout
                </a>
                .
              </div>
            )}
          </div>
        </div>
      </PurchaseLayout>
      <ConfirmationModal
        showModal={showSuccessModal}
        onCloseModal={() => (1 ? {} : setShowSuccessModal(false))}
        headerText="Purchase Request Submitted!"
        btnText="Preview"
        btnActtion={() => {
          // navigate(`/purchase/checkout/${listingId}`);
          navigate(`/purchase/invoice/${referenceId}`);
        }}
      >
        <div className="text-[16px] text-[#5E5E5E]">
          We’ve received your purchase request and you’ll receive an invoice
          shortly.
        </div>
      </ConfirmationModal>
    </>
  );
};

export default Purchase;
